import { useContext, useEffect, useState } from 'react'

import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BiometricsFingerprintIcon, PasskeyIcon } from '@arcade/web/icons/react'
import { ArcadeButton, typography } from '@cash-design-system/react'
import { PasskeyUpsellBlocker } from '@cash-web/protos/squareup/franklin/blockers/passkey_upsell_blocker.pb'
import { useRegisterPasskey } from '@cash-web/shared-auth-feature-main'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { BlockerComponentProps, useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BaseBlocker, BaseBlockerFormFields, SubmitButton } from '../../BaseBlocker/BaseBlocker'

interface PasskeyUpsellElement {
  icon?: string
  subtitle?: string
  body?: string
}

interface ExtendedPasskeyUpsellBlocker extends PasskeyUpsellBlocker {
  elements?: PasskeyUpsellElement[]
}

const iconMap: Record<string, () => React.ReactNode> = {
  PasskeyIcon: () => <PasskeyIcon size={24} />,
  BiometricsFingerprintIcon: () => <BiometricsFingerprintIcon size={24} />,
}

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.l};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`

const BenefitItem = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.m};
  align-items: flex-start;
`

const BenefitIconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
`

const BenefitContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const BenefitTitle = styled.div`
  ${typography.bodyMedium}
`

const BenefitDescription = styled.div`
  ${typography.bodySmall}
  color: ${({ theme }) => theme.colors.textSubtle};
`

enum PasskeyState {
  INITIAL = 'initial',
  REGISTERED = 'registered',
}

export function BlockerPasskeysUpsell({
  blocker,
  skippable,
}: BlockerComponentProps<'passkey_upsell_blocker'> | { blocker?: ExtendedPasskeyUpsellBlocker; skippable?: boolean }) {
  const { t } = useTranslation()
  const form = useForm<BaseBlockerFormFields>()
  const flow = useFlowContext()
  const [passkeyState, setPasskeyState] = useState<PasskeyState>(PasskeyState.INITIAL)
  const [isSkipping, setSkipping] = useState(false)
  const { setError, clearError } = useContext(ErrorContext)

  const { registerPasskey, reset, error, isLoading } = useRegisterPasskey(() => {
    setPasskeyState(PasskeyState.REGISTERED)
  })

  const handleAddPasskey = () => {
    clearError()
    reset()
    registerPasskey()
  }

  // Sync passkey registration errors to ErrorContext for display in BaseBlocker
  useEffect(() => {
    if (error) {
      setError(error)
    }
  }, [error, setError])

  const handleContinue = async () => {
    await flow.skipBlocker()
  }

  const handleSkip = async () => {
    setSkipping(true)
    await flow.skipBlocker()
  }

  const mainText =
    passkeyState === PasskeyState.INITIAL
      ? blocker?.header_text || t('Create a passkey to log in faster', { context: 'blocker main text' })
      : t('Passkey created', { context: 'blocker main text' })

  const secondaryText =
    passkeyState === PasskeyState.INITIAL
      ? undefined
      : t('You can manage your login settings anytime in the Privacy & Security section of your profile.', {
          context: 'blocker secondary text',
        })

  const initialButtons = [
    <SubmitButton
      key="add-passkey"
      text={t('Create passkey', { context: 'button text' })}
      disabled={isLoading}
      isLoading={isLoading}
    />,
  ]

  if (skippable) {
    initialButtons.unshift(
      <ArcadeButton size="cta" prominence="standard" key="skip" isLoading={isSkipping} onClick={handleSkip}>
        {t('Not now', { context: 'button text' })}
      </ArcadeButton>
    )
  }

  const buttons =
    passkeyState === PasskeyState.INITIAL
      ? initialButtons
      : [<SubmitButton key="continue" text={t('Continue', { context: 'button text' })} />]

  return (
    <BaseBlocker
      mainText={mainText}
      secondaryText={secondaryText}
      form={form}
      onSubmit={passkeyState === PasskeyState.INITIAL ? handleAddPasskey : handleContinue}
      buttons={buttons}
    >
      {passkeyState === PasskeyState.INITIAL && (
        <BenefitsList>
          {blocker?.elements && blocker.elements.length > 0 ? (
            blocker.elements.map((element, index) => {
              const IconComponent = element.icon ? iconMap[element.icon] : null
              return (
                <BenefitItem key={index}>
                  {IconComponent && (
                    <BenefitIconWrapper>
                      <IconComponent />
                    </BenefitIconWrapper>
                  )}
                  <BenefitContent>
                    {element.subtitle && <BenefitTitle>{element.subtitle}</BenefitTitle>}
                    {element.body && <BenefitDescription>{element.body}</BenefitDescription>}
                  </BenefitContent>
                </BenefitItem>
              )
            })
          ) : (
            <>
              <BenefitItem>
                <BenefitIconWrapper>
                  <PasskeyIcon size={24} />
                </BenefitIconWrapper>
                <BenefitContent>
                  <BenefitTitle>{t('Create on your device', { context: 'passkey benefit title' })}</BenefitTitle>
                  <BenefitDescription>
                    {t(
                      'Follow instructions on your device or password manager to create a passkey. This will work across synced devices.',
                      { context: 'passkey benefit description' }
                    )}
                  </BenefitDescription>
                </BenefitContent>
              </BenefitItem>
              <BenefitItem>
                <BenefitIconWrapper>
                  <BiometricsFingerprintIcon size={24} />
                </BenefitIconWrapper>
                <BenefitContent>
                  <BenefitTitle>{t('Skip the verification code', { context: 'passkey benefit title' })}</BenefitTitle>
                  <BenefitDescription>
                    {t(
                      'Next time you log in, your device will verify your identity using face, fingerprint, or device PIN.',
                      { context: 'passkey benefit description' }
                    )}
                  </BenefitDescription>
                </BenefitContent>
              </BenefitItem>
            </>
          )}
        </BenefitsList>
      )}
    </BaseBlocker>
  )
}
